import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Select, MenuItem } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { niiService } from '../../services/niiService';
import { NiiRunType, NiiRunStatus, SummaryCard } from './types/nii.types';
import styles from './NiiLandingPage.module.css';

const AuditReportsLanding: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();

  // State
  const [selectedRelease, setSelectedRelease] = useState<string>('');
  const [appliedRelease, setAppliedRelease] = useState<string | null>(null);

  // Fetch summary cards
  const { data: summaryData, isLoading, error } = useQuery({
    queryKey: ['gpl-audit-summary-cards'],
    queryFn: () => niiService.getReleaseSummaryCards(NiiRunType.GPL_AUDIT, 'gplVersion'),
    retry: false,
    refetchOnWindowFocus: false
  });

  // Only show releases with completed reports
  const availableReleases = useMemo(() => {
    if (!summaryData) return [];
    return summaryData.cards
      .filter((card) => card.completedCount > 0)
      .map((card) => card.anchor);
  }, [summaryData]);

  // Fetch all completed GPL audit reports to compute unique node counts per version
  const { data: allReportsData } = useQuery({
    queryKey: ['gpl-audit-all-reports'],
    queryFn: () => niiService.getReports({
      type: NiiRunType.GPL_AUDIT,
      status: NiiRunStatus.COMPLETE
    }),
    refetchOnWindowFocus: false
  });

  // Compute unique node count per gplVersion
  const uniqueNodeCountByVersion = useMemo(() => {
    const reports = allReportsData?.data || [];
    const map = new Map<string, Set<string>>();
    for (const r of reports) {
      const version = r.gplVersion || '';
      if (!map.has(version)) map.set(version, new Set());
      map.get(version)!.add(r.nodeName || 'Unknown');
    }
    const result: Record<string, number> = {};
    map.forEach((nodes, version) => { result[version] = nodes.size; });
    return result;
  }, [allReportsData]);

  // Cards to display
  const displayCards = useMemo(() => {
    if (!summaryData) return [];
    const cards = summaryData.cards.filter((c) => c.completedCount > 0);
    if (appliedRelease) {
      return cards.filter((c) => c.anchor === appliedRelease);
    }
    return cards;
  }, [summaryData, appliedRelease]);

  // Show error notification when summary cards fetch fails.
  useEffect(() => {
    if (!error) return;
    const message = error instanceof AxiosError
      ? error.response?.data?.message || error.message
      : error instanceof Error ? error.message : 'Failed to load audit reports.';
    addNotifications([{
      title: 'Error',
      icon: 'icon-triangle-warning',
      description: message,
      timestamp: new Date(),
      color: 'red'
    }]);
  }, [error]);

  const handleLoad = () => {
    if (!selectedRelease.trim()) return;
    setAppliedRelease(selectedRelease);
  };

  const handleViewRelease = (gplVersion: string) => {
    navigate(`/nii/audit-reports/release/${encodeURIComponent(gplVersion)}`);
  };

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <div
        className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
      >
        <div className={styles.settingsTitleContainer}>
          {/* Header with Create button */}
          <div className={styles.pageHeader}>
            <div>
              <div className={styles.settingsTitle}>Audit Report</div>
              <p className={styles.subtitle}>
                Review and manage your GPL audit reports. GPL audits check node configurations against golden parameter lists.
              </p>
            </div>
            <ReactEDSWrapper component={Button} primary onClick={() => navigate('/nii/audit-reports/new')}>
              <i className="icon icon-plus" style={{ marginRight: '4px' }} />
              Create New Audit
            </ReactEDSWrapper>
          </div>

          {/* Release dropdown + Reset + Load */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>

            <div>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>
                Release Name{' '}
                <i className="icon icon-info" style={{ fontSize: '10px', opacity: 0.6 }} />
              </label>
              <ReactEDSWrapper
                component={Select}
                type="input-single"
                label="Select..."
                value={selectedRelease}
                disabled={isLoading}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(raw: any) => {
                  const next = raw?.target?.value ?? raw;
                  const selected = Array.isArray(next) ? next[0] : next;
                  setSelectedRelease(typeof selected === 'string' ? selected : String(selected ?? ''));
                }}
              >
                {availableReleases.map((release) => (
                  <ReactEDSWrapper key={release} component={MenuItem} value={release}>
                    {release}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignSelf: 'flex-end' }}>
              <ReactEDSWrapper component={Button} primary onClick={handleLoad} disabled={!selectedRelease.trim()}>
                Load
              </ReactEDSWrapper>
            </div>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
              <div className="loader small" />
              <p>Loading audit reports…</p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && displayCards.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <i className="icon icon-info" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
              <div style={{ fontWeight: 500 }}>No GPL audit reports found</div>
              <p style={{ color: '#878787', marginTop: '4px' }}>
                {appliedRelease
                  ? `No completed reports for release "${appliedRelease}".`
                  : 'Create a new audit to get started.'}
              </p>
            </div>
          )}

          {/* Report cards grid */}
          {!isLoading && displayCards.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {displayCards.map((card: SummaryCard) => (
                <div
                  key={card.anchor}
                  style={{
                    border: `1px solid #878787`,
                    padding: '16px 24px',
                    minWidth: '300px',
                    maxWidth: '500px'
                  }}
                >
                  <div style={{ fontWeight: 500, fontSize: '16px', marginBottom: '4px' }}>{card.anchor}</div>
                  <div style={{ color: '#878787', fontSize: '14px', marginBottom: '12px' }}>
                    {uniqueNodeCountByVersion[card.anchor] || 0} nodes
                  </div>
                  <ReactEDSWrapper component={Button} primary onClick={() => handleViewRelease(card.anchor)}>
                    View
                  </ReactEDSWrapper>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditReportsLanding;
